import { toast as rtToast } from "react-toastify";
import i18n from "../i18n/index.js";
import { BACKEND_MESSAGE_TEMPLATES } from "../i18n/backendTemplates";

// `toast` de la app: el de react-toastify, pero todo texto pasa por el i18n al mostrarse.
//
// Muchos avisos muestran el mensaje tal cual lo manda el backend (`data.message`,
// `error.message`…), y los backends hablan en español o en inglés según el servicio. Se
// traduce acá, al mostrar, y nunca en la respuesta: hay vistas que comparan el texto crudo
// del backend (p. ej. el login) y dejarían de funcionar. El catálogo de mensajes del
// backend vive en `i18n/locales/*/backend.json` (y en `locales/es` los que llegan en inglés).
//
// Solo se traduce lo que tiene entrada: `t()` sobre texto arbitrario interpretaría `{{…}}`
// o `$t(…)` del backend, y un texto ya traducido por la vista no se toca. Importar siempre
// desde acá, no desde "react-toastify".

// Mensajes del backend con datos variables ("password debe tener al menos 8 caracteres"):
// la clave lleva {{v0}}, {{v1}}… donde el backend interpola, y acá se reconoce el texto
// con un patrón. Primero los de más texto fijo, para que gane el más específico.
let templatePatterns;
function getTemplatePatterns() {
  if (!templatePatterns) {
    const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    templatePatterns = BACKEND_MESSAGE_TEMPLATES.map((key) => {
      const parts = key.split(/\{\{(v\d+)\}\}/);
      const names = parts.filter((_, i) => i % 2 === 1);
      const source = parts.map((part, i) => (i % 2 === 1 ? "(.*?)" : escape(part))).join("");
      const fixed = parts.filter((_, i) => i % 2 === 0).join("").length;
      return { key, names, regex: new RegExp(`^${source}$`, "s"), fixed };
    }).sort((a, b) => b.fixed - a.fixed);
  }
  return templatePatterns;
}

// Los errores de validación múltiples llegan unidos con " · " (errorHandler,
// apiErrorMessage): cada parte se traduce por separado.
const PART_SEPARATOR = " · ";

function translateOne(text, depth) {
  if (i18n.exists(text)) return i18n.t(text);
  for (const { key, names, regex } of getTemplatePatterns()) {
    const match = regex.exec(text);
    if (match && i18n.exists(key)) {
      // El dato interpolado a veces es otro mensaje del backend ("No pudimos crear la
      // cuenta: <motivo>"): se traduce un nivel, para no mezclar idiomas en el aviso.
      const values = Object.fromEntries(
        names.map((name, i) => [name, depth > 0 ? match[i + 1] : translateOne(match[i + 1], depth + 1)]),
      );
      return i18n.t(key, values);
    }
  }
  return text;
}

function translate(content) {
  if (typeof content !== "string" || !content) return content;
  const whole = translateOne(content, 0);
  if (whole !== content || !content.includes(PART_SEPARATOR)) return whole;
  return content.split(PART_SEPARATOR).map((part) => translateOne(part, 0)).join(PART_SEPARATOR);
}

// `{ render: "texto" }` (toast.update / toast.promise) o el texto directo.
function translateRender(value) {
  if (value && typeof value === "object" && "render" in value) {
    return { ...value, render: translate(value.render) };
  }
  return translate(value);
}

const withText = (method) => (content, ...rest) => rtToast[method](translate(content), ...rest);

export const toast = (content, ...rest) => rtToast(translate(content), ...rest);

toast.success = withText("success");
toast.error = withText("error");
toast.info = withText("info");
toast.warning = withText("warning");
toast.warn = withText("warn");
toast.loading = withText("loading");
toast.dark = withText("dark");

toast.update = (toastId, options, ...rest) =>
  rtToast.update(toastId, options ? translateRender(options) : options, ...rest);

toast.promise = (promise, messages, ...rest) => {
  if (!messages) return rtToast.promise(promise, messages, ...rest);
  const translated = { ...messages };
  for (const key of ["pending", "success", "error"]) {
    if (key in translated) translated[key] = translateRender(translated[key]);
  }
  return rtToast.promise(promise, translated, ...rest);
};

// El resto de la API sin cambios (dismiss, isActive, done, onChange, POSITION, TYPE…).
for (const key of ["dismiss", "isActive", "done", "onChange", "clearWaitingQueue", "POSITION", "TYPE"]) {
  Object.defineProperty(toast, key, { enumerable: true, get: () => rtToast[key] });
}
